import React, { FC, useState } from 'react';
import { createPortal } from 'react-dom';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DownloadIcon from '@mui/icons-material/Download';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { SxProps, Theme } from '@mui/material/styles';

interface DownloadButtonProps {
  url: string;
  fileName: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'text' | 'outlined' | 'contained';
  disabled?: boolean;
  sx?: SxProps<Theme>;
  /**
   * When true, renders a compact 28x28 icon-only square matching other action
   * buttons (used in the Workbench version-history row alongside the
   * Transcript and Send buttons so all three look uniform).
   */
  iconOnly?: boolean;
}

/**
 * Reusable download button component that uses presigned S3 URLs with Content-Disposition
 * @reason Checks file availability via a small GET request before triggering the full download.
 * Supports files >2GB via direct <a> navigation after the check.
 */
const DownloadButton: FC<DownloadButtonProps> = ({
  url,
  fileName,
  size = 'small',
  variant = 'contained',
  disabled = false,
  sx = {},
  iconOnly = false,
}) => {
  const [checking, setChecking] = useState(false);
  const [showNotReady, setShowNotReady] = useState(false);

  const handleClick = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    if (checking) return;

    setChecking(true);
    try {
      // Use a GET request with Range header to fetch only the first byte.
      // HEAD is blocked by S3 CORS, but GET is allowed.
      const controller = new AbortController();
      const response = await fetch(url, {
        method: 'GET',
        headers: { Range: 'bytes=0-0' },
        signal: controller.signal,
      });
      // Abort immediately — we only needed the status code
      controller.abort();

      if (response.ok || response.status === 206) {
        // File exists — trigger download via a temporary <a> tag
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        setShowNotReady(true);
      }
    } catch (err: unknown) {
      // Ignore the AbortError from our own controller.abort()
      if (err instanceof DOMException && err.name === 'AbortError') {
        // The abort happened after we got the response, so the file exists.
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        setShowNotReady(true);
      }
    } finally {
      setChecking(false);
    }
  };

  return (
    <>
      {iconOnly ? (
        <IconButton
          size="small"
          disabled={disabled || checking}
          onClick={handleClick}
          title={checking ? 'Checking…' : 'Download'}
          aria-label={checking ? 'Checking' : 'Download'}
          sx={{
            width: 28,
            height: 28,
            border: '1px solid #e0e0e0',
            borderRadius: '6px',
            background: '#ffffff',
            color: '#666',
            padding: 0,
            transition: 'all 0.15s ease',
            '&:hover': {
              borderColor: '#f1b400',
              color: '#f1b400',
              background: '#fffbeb',
            },
            ...sx,
          }}
        >
          <DownloadIcon sx={{ fontSize: 16 }} />
        </IconButton>
      ) : (
        <Button
          size={size}
          variant={variant}
          disabled={disabled || checking}
          startIcon={<DownloadIcon />}
          onClick={handleClick}
          sx={{ paddingLeft: 1, paddingRight: 1, maxHeight: '32px', ...sx }}
        >
          {checking ? 'Checking...' : 'Download'}
        </Button>
      )}
      {createPortal(
        <Snackbar
          open={showNotReady}
          autoHideDuration={4000}
          onClose={() => setShowNotReady(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setShowNotReady(false)}
            severity="info"
            variant="filled"
            sx={{ width: '100%' }}
          >
            Video is not ready yet. Please try again in a moment.
          </Alert>
        </Snackbar>,
        document.body,
      )}
    </>
  );
};

export default DownloadButton;
